import { DIRECTION, PATHING_TYPES } from "./constants.ts";
import { BinaryHeap } from "./BinaryHeap.ts";
import {
  angleDifference,
  distanceBetweenEntities,
  distanceBetweenPoints,
  offset,
  Point,
} from "./math.ts";
import { memoize } from "./memoize.ts";
import { Tile } from "./Tile.ts";
import { Footprint, Pathing, PathingEntity, TargetEntity } from "./types.ts";
import {
  PATHING_WALK_ANGLE_DIFFERENCE,
  PATHING_WALK_IGNORE_DISTANCE,
} from "../constants.ts";
import { Entity } from "../types.ts";
import {
  pointToTilemap as calculatePointToTilemap,
  xTileToWorld as tileToWorldX,
  xWorldToTile as worldToTileX,
  yTileToWorld as tileToWorldY,
  yWorldToTile as worldToTileY,
} from "./coordinates.ts";

// Raw-coordinate versions of trueMinX/trueMaxX for tile-native _linearPathable
// Avoids Point object allocation by accepting px, py directly
const trueMinXRaw = (
  px: number,
  py: number,
  radiusSq: number,
  yCoord: number,
  offValue: number,
): number => {
  if (Math.abs(py - yCoord) - 0.5 <= 1) return offValue;
  const yRef = yCoord > py ? yCoord : yCoord + 1;
  const squared = -(py * py) + 2 * py * yRef + radiusSq - yRef * yRef;
  return Math.floor(px - Math.sign(squared) * Math.abs(squared) ** 0.5);
};

const trueMaxXRaw = (
  px: number,
  py: number,
  radiusSq: number,
  yCoord: number,
  offValue: number,
): number => {
  if (Math.abs(py - yCoord) - 0.5 <= 1) return offValue;
  const yRef = yCoord > py ? yCoord : yCoord + 1;
  const squared = -(py * py) + 2 * py * yRef + radiusSq - yRef * yRef;
  return Math.floor(px + Math.sign(squared) * Math.abs(squared) ** 0.5);
};

const PLAYER_PATHING_BUDGET = 5000;
const MIN_UNIT_BUDGET = 50;

let debugging = false;
// const elems: HTMLElement[] = [];
// export const toggleDebugging = (): void => {
// 	if (debugging) elems.forEach((elem) => arena.removeChild(elem));

// 	debugging = !debugging;
// };
try {
  Object.defineProperty(globalThis, "debugging", {
    set: (value) => (debugging = value),
    get: () => debugging,
  });
} catch {
  /* do nothing */
}

const DEFAULT_RESOLUTION = 1;

const MAX_TRIES = 3481; // 59**2
const EPSILON = Number.EPSILON * 100;

// interface BaseEntity {
//   radius: number;
//   blocksPathing?: Pathing;
//   tilemap?: Footprint;
//   pathing?: Pathing;
//   requiresPathing?: Pathing;
//   tilemap?: Footprint;
//   structure?: boolean;
// }

// type SimpleEntity = BaseEntity & { x: number; y: number };
// type ComplexEntity = BaseEntity & { position: { x: number; y: number } };

// type Entity = SimpleEntity | ComplexEntity;

interface Cache {
  _linearPathable: (
    ...args: Parameters<typeof PathingMap.prototype._linearPathable>
  ) => ReturnType<typeof PathingMap.prototype._linearPathable>;
  _pathable: (
    ...args: Parameters<typeof PathingMap.prototype._pathable>
  ) => ReturnType<typeof PathingMap.prototype._pathable>;
  pointToTilemap: (
    ...args: Parameters<typeof PathingMap.prototype.pointToTilemap>
  ) => ReturnType<typeof PathingMap.prototype.pointToTilemap>;
}

//   0,   0, 255 = 0
//   0, 255, 255 = 0.25
//   0, 255,   0 = 0.5
// 255, 255,   0 = 0.75
// 255,   0,   0 = 1
// const r = (v: number) => (v < 0.5 ? 0 : v < 0.75 ? (v - 0.5) * 4 : 1);
// const g = (v: number) => (v < 0.25 ? v * 4 : v < 0.75 ? 1 : (1 - v) * 4);
// const b = (v: number) => (v < 0.25 ? 1 : v < 0.5 ? (0.5 - v) * 4 : 0);

// const placeTile = (x: number, y: number, v: number) => {
// 	const div = document.createElement("div");
// 	div.style.position = "absolute";
// 	div.style.top = y * 16 + "px";
// 	div.style.left = x * 16 + "px";
// 	div.style.zIndex = "10000";
// 	div.style.width = "16px";
// 	div.style.height = "16px";
// 	div.style.background = `rgba(${r(v) * 255}, ${g(v) * 255}, ${
// 		b(v) * 255
// 	}, 0.5)`;
// 	// div.cell = this.grid[ y ][ x ];
// 	arena.appendChild(div);
// 	elems.push(div);
// };

// Estimated cost remaining
const h = (a: Point, b: Point) =>
  Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);

export class PathingMap {
  readonly resolution: number;
  readonly tileResolution: number;
  readonly layers?: number[][];
  readonly heightWorld: number;
  readonly widthWorld: number;
  readonly heightMap: number;
  readonly widthMap: number;
  readonly grid: (Tile | undefined)[][];
  private readonly pathing: Pathing[][];

  // debugging
  // private _elem?: HTMLDivElement;

  // Maps entities to tiles
  private readonly entities: Map<PathingEntity, Tile[]> = new Map();

  // Precomputed context for _linearPathable, set during path()
  private _losCtx?: {
    radius: number;
    pathing: Pathing;
    radiusTileOffset: number;
  };

  // Per-player pathing iteration tracking
  private readonly pathingIterationsPerPlayer = new Map<string, number>();

  trackPathingIteration(owner: string | undefined): boolean {
    if (!owner) return false;
    const count = (this.pathingIterationsPerPlayer.get(owner) ?? 0) + 1;
    this.pathingIterationsPerPlayer.set(owner, count);
    return count > PLAYER_PATHING_BUDGET;
  }

  getPlayerPathingBudgetRemaining(owner: string | undefined): number {
    if (!owner) return MIN_UNIT_BUDGET;
    const used = this.pathingIterationsPerPlayer.get(owner) ?? 0;
    return Math.max(MIN_UNIT_BUDGET, PLAYER_PATHING_BUDGET - used);
  }

  getPathingStats(): Map<string, number> {
    return this.pathingIterationsPerPlayer;
  }

  resetPathingStats(): Map<string, number> {
    const stats = new Map(this.pathingIterationsPerPlayer);
    this.pathingIterationsPerPlayer.clear();
    return stats;
  }

  constructor({
    pathing,
    resolution = DEFAULT_RESOLUTION,
    tileResolution = 1,
    layers,
  }: {
    pathing: Pathing[][];
    resolution?: number;
    tileResolution?: number;
    layers?: number[][];
  }) {
    this.resolution = resolution;
    this.tileResolution = tileResolution;
    this.pathing = pathing;
    this.layers = layers;

    // World dimensions: when tileResolution is used, the pathing array is higher resolution
    // So world size = pathing size / tileResolution
    this.heightWorld = pathing.length / tileResolution;
    this.widthWorld = pathing[0].length / tileResolution;

    // Map dimensions are tiles: world * resolution
    // resolution is tiles per world unit (e.g., 4 means each world unit has 4x4 tiles)
    this.heightMap = this.heightWorld * resolution;
    this.widthMap = this.widthWorld * resolution;

    // Initialize grid with empty arrays - tiles created lazily
    this.grid = [];
    for (let y = 0; y < this.heightMap; y++) {
      this.grid[y] = [];
    }

    if (debugging) {
      const oldPath = this.path;
      this.path = (...args) => {
        const ret = oldPath.call(this, ...args);
        return ret;
      };

      const oldRecheck = this.recheck;
      this.recheck = (...args) => {
        const ret = oldRecheck.call(this, ...args);
        return ret;
      };
    }
  }

  /**
   * Lazily creates a tile at the given coordinates if it doesn't exist.
   * Neighbors are NOT set up eagerly to avoid cascading tile creation.
   * Instead, they're set up lazily via getNeighbors().
   * Returns undefined if coordinates are out of bounds.
   */
  private getTile(x: number, y: number): Tile | undefined {
    // Check bounds first
    if (x < 0 || y < 0 || x >= this.widthMap || y >= this.heightMap) {
      return undefined;
    }

    // Return existing tile if already created
    const existing = this.grid[y][x];
    if (existing) return existing;

    // Calculate which pathing cell this tile belongs to
    const tilesPerPathingCell = this.resolution / this.tileResolution;
    const pathingX = Math.floor(x / tilesPerPathingCell);
    const pathingY = Math.floor(y / tilesPerPathingCell);

    // Verify pathing data exists for this tile
    if (this.pathing[pathingY]?.[pathingX] === undefined) {
      return undefined;
    }

    // Create the tile
    const tile = new Tile(
      x,
      y,
      x / this.resolution,
      y / this.resolution,
      this.pathing[pathingY][pathingX],
    );
    this.grid[y][x] = tile;

    return tile;
  }

  /**
   * Lazily gets the neighbors for a tile. Sets up the neighbor
   * relationships on first access.
   */
  private getNeighbors(tile: Tile): Tile[] {
    // If neighbors already set up, return them
    if (tile.nodes.length > 0) return tile.nodes;

    const { x, y } = tile;
    const nodes: Tile[] = tile.nodes;

    // Important: We add neighbors in consistent order (down, right, up, left)
    // to match the original eager initialization order

    // Below
    const below = this.getTile(x, y + 1);
    if (below) nodes.push(below);

    // Right
    const right = this.getTile(x + 1, y);
    if (right) nodes.push(right);

    // Above
    const above = this.getTile(x, y - 1);
    if (above) nodes.push(above);

    // Left
    const left = this.getTile(x - 1, y);
    if (left) nodes.push(left);

    return nodes;
  }

  setPathing(
    x: number,
    y: number,
    pathing: Pathing,
    scale = this.resolution,
  ) {
    for (let dy = y * scale; dy < (y + 1) * scale; dy++) {
      for (let dx = x * scale; dx < (x + 1) * scale; dx++) {
        const tile = this.getTile(dx, dy);
        if (!tile) continue;
        tile.originalPathing = pathing;
        tile.recalculatePathing();
      }
    }
  }

  /**
   * Internals of PathingMap#pathable. Not private for interface typing
   * reasons.
   */
  _pathable(
    map: Footprint,
    xTile: number,
    yTile: number,
    test?: (tile: Tile) => boolean,
  ): boolean {
    if (
      xTile < 0 ||
      yTile < 0 ||
      xTile >= this.widthMap ||
      yTile >= this.heightMap
    ) {
      return false;
    }

    let i = 0;

    for (let y = yTile + map.top; y < yTile + map.height + map.top; y++) {
      for (
        let x = xTile + map.left;
        x < xTile + map.width + map.left;
        x++, i++
      ) {
        if (
          y < 0 || y >= this.heightMap || x < 0 || x >= this.widthMap
        ) {
          return false;
        }
        const tile = this.getTile(x, y);
        if (
          !tile ||
          !tile.pathable(map.map[i]) ||
          (test && !test(tile))
        ) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Fast terrain-only check for a single point.
   * Just checks if the terrain tile allows the given pathing type.
   */
  terrainPathablePoint(
    xWorld: number,
    yWorld: number,
    pathing: Pathing,
  ): boolean {
    const xTile = this.xWorldToTile(xWorld);
    const yTile = this.yWorldToTile(yWorld);

    if (
      xTile < 0 ||
      yTile < 0 ||
      xTile >= this.widthMap ||
      yTile >= this.heightMap
    ) {
      return false;
    }

    const tile = this.getTile(xTile, yTile);
    return tile ? tile.pathable(pathing) : false;
  }

  pathable(entity: PathingEntity, xWorld?: number, yWorld?: number): boolean {
    const position = entity.position;
    if (xWorld === undefined) xWorld = position.x;
    if (yWorld === undefined) yWorld = position.y;

    const xTile = this.xWorldToTile(xWorld);
    const yTile = this.yWorldToTile(yWorld);
    const map = entity.requiresTilemap ?? entity.tilemap ??
      this.pointToTilemap(xWorld, yWorld, entity.radius, {
        type: entity.requiresPathing ?? entity.pathing,
      });

    return this.withoutEntity(entity, () => this._pathable(map, xTile, yTile));
  }

  /**
   * Temporarily removes an entity from the PathingMap, invokes the passed
   * function, re-adds the entity, and returns the result of the function.
   */
  withoutEntity<A>(entity: PathingEntity, fn: () => A): A {
    const removed = this.entities.has(entity);
    if (removed) this.removeEntity(entity);

    const result = fn();

    if (removed) this.addEntity(entity);

    return result;
  }

  /**
   * Given an initial position `(xWorld, yWorld)`, returns the nearest point
   * the entity can be placed, measured by euclidean distance (thus would form
   * a circle instead of square for repeated placements).
   */
  nearestPathing(
    xWorld: number,
    yWorld: number,
    entity: PathingEntity,
    test?: (tile: Tile) => boolean,
  ): Point {
    return this.nearestPathingGen(xWorld, yWorld, entity, test).next().value!;
  }

  /**
   * Given an initial position `(xWorld, yWorld)`, returns the nearest points
   * the entity can be placed, measured by euclidean distance (thus would form
   * a circle instead of square for repeated placements).
   */
  private *nearestPathingGen(
    xWorld: number,
    yWorld: number,
    entity: PathingEntity,
    test?: (tile: Tile) => boolean,
  ): Generator<Point, Point> {
    const tile = this.entityToTile(entity, { x: xWorld, y: yWorld });

    // Calculate input from non-entity input
    const target = { x: xWorld, y: yWorld };

    // Calculate constants from entity
    const pathing = entity.requiresPathing === undefined
      ? entity.pathing
      : entity.requiresPathing;
    if (pathing === undefined) throw new Error("entity has no pathing");
    const minimalTilemap = entity.requiresTilemap ?? entity.tilemap ??
      this.pointToTilemap(
        entity.radius,
        entity.radius,
        entity.radius,
        { type: pathing },
      );
    const radiusOffset = entity.radius % (1 / this.resolution);
    const offset = (point: { xWorld: number; yWorld: number }) => ({
      x: point.xWorld + radiusOffset,
      y: point.yWorld + radiusOffset,
    });

    // Create our heap
    const distance = (a: Point, b: Point) =>
      (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
    // This won't desync anything
    // eslint-disable-next-line no-restricted-syntax
    const tag = Math.random();
    const heap = new BinaryHeap((node: Tile) => node.__np ?? 0);

    // Seed our heap
    const start = tile;
    start.__npTag = tag;
    start.__np = distance(target, offset(start));
    heap.push(start);

    let prev: Point | undefined;

    // Find a node!
    while (heap.length) {
      const current = heap.pop();

      if (
        current.pathable(pathing) &&
        this._pathable(minimalTilemap, current.x, current.y, test)
      ) {
        if (prev) yield prev;
        prev = offset(current);
      }

      this.getNeighbors(current).forEach((neighbor) => {
        if (neighbor.__npTag === tag) return;
        neighbor.__npTag = tag;
        neighbor.__np = distance(target, offset(neighbor));

        heap.push(neighbor);
      });
    }

    // Found nothing, return input
    return prev ?? { x: xWorld, y: yWorld };
  }

  private _layer(xTile: number, yTile: number): number | undefined {
    if (!this.layers) return;
    if (yTile < 0) return;

    const xLayerTile = Math.floor(xTile / this.tileResolution);
    const yLayerTile = Math.floor(yTile / this.tileResolution);

    if (this.layers.length <= yLayerTile) return;
    return this.layers[yLayerTile]?.[xLayerTile];
  }

  /**
   * Returns the layer for a given world coordinate. Some pathing calculations
   * are constrained to specific layers, such as
   * PathingMap#nearestSpiralPathing.
   */
  layer(xWorld: number, yWorld: number): number | undefined {
    if (!this.layers) return;
    if (yWorld < 0) return;

    const xLayerTile = Math.floor(
      xWorld * this.resolution / this.tileResolution,
    );
    const yLayerTile = Math.floor(
      yWorld * this.resolution / this.tileResolution,
    );

    if (this.layers.length <= yLayerTile) return;
    return this.layers[yLayerTile]?.[xLayerTile];
  }

  /**
   * Given an initial position `(xWorld, yWorld)`, returns the nearest point
   * on the same layer at which the entity can be placed, as discovered by
   * spiraling out (thus would form a square instead of circle for repeated
   * placements).
   */
  nearestSpiralPathing(
    xWorld: number,
    yWorld: number,
    entity: PathingEntity,
    layer = this.layer(xWorld, yWorld),
  ): Point {
    const originalX = xWorld;
    const originalY = yWorld;

    let xTile = this.xWorldToTile(xWorld);
    let yTile = this.yWorldToTile(yWorld);

    let attemptLayer = this._layer(xTile, yTile);

    if (layer === attemptLayer) {
      const tilemap = entity.requiresTilemap ?? entity.tilemap;
      if (tilemap) {
        if (this._pathable(tilemap, xTile, yTile)) {
          return { x: this.xTileToWorld(xTile), y: this.yTileToWorld(yTile) };
        }
      } else if (
        this._pathable(
          this.pointToTilemap(
            xWorld,
            yWorld,
            entity.radius,
            {
              type: entity.requiresPathing === undefined
                ? entity.pathing
                : entity.requiresPathing,
            },
          ),
          xTile,
          yTile,
        )
      ) return { x: xWorld, y: yWorld };
    }

    const xMiss = Math.abs(xWorld * this.resolution - xTile);
    const yMiss = Math.abs(yWorld * this.resolution - yTile);

    // todo mirror WC3 for equal misses
    // 0 down, 1 left, 2 up, 3 right
    let direction = Math.abs(0.5 - xMiss) > Math.abs(0.5 - yMiss)
      ? xMiss < 0.5 ? DIRECTION.LEFT : DIRECTION.RIGHT
      : yMiss < 0.5 && yMiss >= 0
      ? DIRECTION.UP
      : DIRECTION.DOWN;

    let steps = 0;
    const stride = entity.requiresTilemap ?? entity.tilemap ? 2 : 1;
    let initialSteps = 0;

    let remainingTries = MAX_TRIES;

    let minimalTilemap = entity.requiresTilemap ?? entity.tilemap;
    let offset;
    if (minimalTilemap) {
      offset = {
        x: minimalTilemap.left / this.resolution -
          (minimalTilemap.width % 4 === 0 ? 0 : 0.25),
        y: minimalTilemap.top / this.resolution +
          (minimalTilemap.height % 4 === 0 ? 0 : 0.25),
      };
    } else {
      minimalTilemap = this.pointToTilemap(
        entity.radius,
        entity.radius,
        entity.radius,
        { type: entity.requiresPathing ?? entity.pathing },
      );
      offset = {
        x: entity.radius % (1 / this.resolution),
        y: entity.radius % (1 / this.resolution),
      };
    }

    const tried = [];
    const initialTile = this.getTile(xTile, yTile);
    if (initialTile) tried.push(initialTile);

    while (
      !this._pathable(minimalTilemap, xTile, yTile) ||
      (layer !== undefined && attemptLayer !== layer)
    ) {
      if (!remainingTries--) return { x: originalX, y: originalY };

      switch (direction) {
        case DIRECTION.DOWN:
          yTile += stride;
          break;
        case DIRECTION.RIGHT:
          xTile += stride;
          break;
        case DIRECTION.UP:
          yTile -= stride;
          break;
        case DIRECTION.LEFT:
          xTile -= stride;
          break;
      }

      const tile = this.getTile(xTile, yTile);
      if (tile) tried.push(tile);

      if (steps === 0) {
        steps = initialSteps;
        if (direction === DIRECTION.DOWN || direction === DIRECTION.UP) {
          initialSteps++;
        }
        direction = (direction + 1) % 4;
      } else steps--;

      attemptLayer = this._layer(xTile, yTile);
    }

    return {
      x: this.xTileToWorld(xTile) + offset.x,
      y: this.yTileToWorld(yTile) + offset.y,
    };
  }

  worldToTile(world: Point): Tile {
    const tile = this.getTile(
      this.xWorldToTile(world.x),
      this.yWorldToTile(world.y),
    );
    if (!tile) {
      throw new Error(
        `worldToTile called with out-of-bounds coordinates: (${world.x}, ${world.y})`,
      );
    }
    return tile;
  }

  xWorldToTile(x: number): number {
    return worldToTileX(x, this.resolution);
  }

  yWorldToTile(y: number): number {
    return worldToTileY(y, this.resolution);
  }

  xTileToWorld(x: number): number {
    return tileToWorldX(x, this.resolution);
  }

  yTileToWorld(y: number): number {
    return tileToWorldY(y, this.resolution);
  }

  /**
   * Calculates a tilemap/footprint required to place something at `(xWorld,
   * yWorld)` with `radius`.
   */
  pointToTilemap(
    xWorld: number,
    yWorld: number,
    radius = 0,
    { type = PATHING_TYPES.WALKABLE } = {},
  ): Footprint {
    return calculatePointToTilemap(
      xWorld,
      yWorld,
      radius,
      this.resolution,
      this.widthWorld,
      { type },
    );
  }

  private yBoundTile(yIndex: number): number {
    return Math.max(Math.min(yIndex, this.heightMap - 1), 0);
  }

  private xBoundTile(xIndex: number): number {
    return Math.max(Math.min(xIndex, this.widthMap - 1), 0);
  }

  // step(
  //   entity: PathingEntity,
  //   target: TargetEntity | Readonly<Point>,
  //   distance: number,
  // ) {
  //   const removed = this.entities.has(entity);
  //   if (removed) this.removeEntity(entity);

  //   // We assume an entity shoved into the top left corner is good
  //   const pathing = entity.requiresPathing === undefined
  //     ? entity.pathing
  //     : entity.requiresPathing;
  //   if (pathing === undefined) throw new Error("entity has no pathing");
  //   // const minimalTilemap = this.pointToTilemap(
  //   //   entity.radius,
  //   //   entity.radius,
  //   //   entity.radius,
  //   //   { type: pathing },
  //   // );

  //   // const offset = entity.radius % (1 / this.resolution);
  //   // const startReal = {
  //   //   x: entity.position.x * this.resolution,
  //   //   y: entity.position.y * this.resolution,
  //   // };

  //   const targetPosition = "x" in target ? target : target.position;

  //   // const startTile = this.entityToTile(entity);

  //   const angle = Math.atan2(
  //     targetPosition.y - entity.position.y,
  //     targetPosition.x - entity.position.x,
  //   );

  //   // Simple: diag
  //   {
  //     const diag = {
  //       x: entity.position.x + distance * Math.cos(angle),
  //       y: entity.position.y + distance * Math.sin(angle),
  //     };
  //     if (this.linearPathable(entity, entity.position, diag)) return diag;
  //   }

  //   const dirs = closestCardinalDirections(angle);
  //   for (const [x, y] of dirs) {
  //     const point = {
  //       x: entity.position.x + distance * x,
  //       y: entity.position.y + distance * y,
  //     };
  //     if (this.linearPathable(entity, entity.position, point)) return point;
  //   }
  // }

  // Adapted from https://github.com/bgrins/javascript-astar/blob/master/astar.js
  // towards Theta*
  // This gets really sad when a path is not possible
  /**
   * Calculates the shortest path for an entity to reach `target`. If a path
   * is not possible, or is extremely long, a partial path will be returned.
   */
  path(
    entity: PathingEntity,
    target: TargetEntity | Readonly<Point>,
    {
      start = entity.position,
      distanceFromTarget,
      removeMovingEntities = true,
    }: {
      start?: Point;
      distanceFromTarget?: number;
      removeMovingEntities?: boolean;
    } = {},
  ): Point[] {
    if (typeof entity.radius !== "number") {
      throw new Error("Can only path find radial entities");
    }

    // If already in range, just return start
    if (
      typeof distanceFromTarget === "number" && "position" in target &&
      distanceBetweenEntities(entity, target, distanceFromTarget) <
        distanceFromTarget
      // Should I return start?
    ) return [];

    if (distanceFromTarget) distanceFromTarget *= this.resolution;

    const losCache = new Map<Tile, Map<Tile, boolean>>();
    const pathableCache = new Map<number, boolean>();
    const cache: Cache = {
      _linearPathable: (_entity, startTile, endTile) => {
        let inner = losCache.get(startTile);
        if (inner) {
          const cached = inner.get(endTile);
          if (cached !== undefined) return cached;
        } else {
          inner = new Map();
          losCache.set(startTile, inner);
        }
        const result = this._linearPathable(_entity, startTile, endTile);
        inner.set(endTile, result);
        return result;
      },
      _pathable: (map, x, y) => {
        const key = x * this.heightMap + y;
        const cached = pathableCache.get(key);
        if (cached !== undefined) return cached;
        const result = this._pathable(map, x, y);
        pathableCache.set(key, result);
        return result;
      },
      pointToTilemap: memoize((...args) => this.pointToTilemap(...args)),
    };

    this._losCtx = {
      radius: entity.radius * this.resolution -
        EPSILON * entity.radius * this.widthWorld * this.resolution,
      pathing: (entity.requiresPathing ?? entity.pathing)!,
      radiusTileOffset: (entity.radius % (1 / this.resolution)) *
        this.resolution,
    };

    const removed = this.entities.has(entity);
    if (removed) this.removeEntity(entity);

    const removedMovingEntities = new Set<PathingEntity>();
    if (removeMovingEntities) {
      for (const e of this.entities.keys()) {
        if (!e.order || !("path" in e.order) || !e.order.path?.length) {
          continue;
        }

        // Don't remove entities that are targeting the pathing entity
        if ("targetId" in e.order && e.order.targetId === entity.id) {
          continue;
        }

        // Pre-filter: skip expensive distanceBetweenEntities for distant entities
        const dx = e.position.x - entity.position.x;
        const dy = e.position.y - entity.position.y;
        const maxRadius = (e.radius ?? 0) + (entity.radius ?? 0);
        const threshold = PATHING_WALK_IGNORE_DISTANCE + maxRadius;
        if (dx * dx + dy * dy > threshold * threshold) {
          removedMovingEntities.add(e);
          this.removeEntity(e);
          continue;
        }

        const dist = distanceBetweenEntities(
          e,
          entity,
          PATHING_WALK_IGNORE_DISTANCE,
        );
        const aDist = Math.abs(angleDifference(
          Math.atan2(
            e.order.path[0].y - e.position.y,
            e.order.path[0].x - e.position.x,
          ),
          Math.atan2(
            ("x" in target ? target.y : target.position.y) -
              entity.position.y,
            ("x" in target ? target.x : target.position.x) -
              entity.position.x,
          ),
        ));
        if (
          dist < PATHING_WALK_IGNORE_DISTANCE &&
          aDist > PATHING_WALK_ANGLE_DIFFERENCE
        ) continue;
        removedMovingEntities.add(e);
        this.removeEntity(e);
      }
    }

    // We assume an entity shoved into the top left corner is good
    const pathing = entity.requiresPathing === undefined
      ? entity.pathing
      : entity.requiresPathing;
    if (pathing === undefined) throw new Error("entity has no pathing");
    const minimalTilemap = cache.pointToTilemap(
      entity.radius,
      entity.radius,
      entity.radius,
      { type: pathing },
    );

    const entityAtTile = { ...entity, position: { x: 0, y: 0 } };

    const offset = entity.radius % (1 / this.resolution);
    const startReal = {
      x: start.x * this.resolution,
      y: start.y * this.resolution,
    };

    const rawTarget = "x" in target ? target : target.position;
    const targetRadius = "x" in target
      ? entity.radius
      : ("radius" in target ? target.radius as number : entity.radius);
    const targetPosition = {
      x: Math.max(
        targetRadius,
        Math.min(rawTarget.x, this.widthWorld - targetRadius),
      ),
      y: Math.max(
        targetRadius,
        Math.min(rawTarget.y, this.heightWorld - targetRadius),
      ),
    };

    // Get start tile - if not pathable, find nearest pathable tile
    let startTile = this.entityToTile(entity);
    const startPathable = startTile &&
      startTile.pathable(pathing) &&
      cache._pathable(minimalTilemap, startTile.x, startTile.y);

    if (!startPathable) {
      // Entity has already been removed from pathingMap, so we can directly use nearestPathing
      const nearestStart = this.nearestPathing(start.x, start.y, entity);

      // Check if nearest pathable tile is too far - prevent jumps/teleports
      const maxDistanceFromStart = 0.5; // Allow small adjustments only
      const distanceSquared = (nearestStart.x - start.x) ** 2 +
        (nearestStart.y - start.y) ** 2;

      if (distanceSquared > maxDistanceFromStart ** 2) {
        // Too far to reasonably path - return empty path
        this._losCtx = undefined;
        if (removed) this.addEntity(entity);
        for (const entity of removedMovingEntities) this.addEntity(entity);
        return [];
      }

      const nearestTile = this.getTile(
        Math.round((nearestStart.x - offset) * this.resolution),
        Math.round((nearestStart.y - offset) * this.resolution),
      );
      if (!nearestTile) {
        this._losCtx = undefined;
        if (removed) this.addEntity(entity);
        for (const entity of removedMovingEntities) this.addEntity(entity);
        return [];
      }
      startTile = nearestTile;
    }

    // For target, if the exact spot is pathable, we aim towards that; otherwise the nearest spot
    const targetTile = this.entityToTile(entity, targetPosition);

    const targetPathable = targetTile &&
      targetTile.pathable(pathing) &&
      this.pathable(entity, targetPosition.x, targetPosition.y);

    const targetReal = {
      x: targetPosition.x * this.resolution,
      y: targetPosition.y * this.resolution,
    };

    const endTag = Math.random();
    const endHeap = new BinaryHeap(
      (node: Tile) => node.__endRealPlusEstimatedCost ?? 0,
    );
    let endBest = targetTile;
    const endTiles = new Set([targetTile]);

    if (targetPathable) {
      const targetClosestReal = targetPathable
        ? {
          x: targetPosition.x * this.resolution,
          y: targetPosition.y * this.resolution,
        }
        : targetTile;

      // If we start and end on the same tile, just move between them
      if (startTile === targetTile && this.pathable(entity)) {
        this._losCtx = undefined;
        if (removed) this.addEntity(entity);
        for (const entity of removedMovingEntities) this.addEntity(entity);
        return [
          { x: start.x, y: start.y },
          {
            x: targetClosestReal.x / this.resolution,
            y: targetClosestReal.y / this.resolution,
          },
        ];
      }

      endHeap.push(targetTile);
      targetTile.__endTag = endTag;
      targetTile.__endRealCostFromOrigin = distanceFromTarget
        ? Math.max(
          h(targetReal, targetTile) - distanceFromTarget,
          0,
        )
        : h(targetClosestReal, targetTile);
      targetTile.__endEstimatedCostRemaining = h(targetTile, startReal);
      targetTile.__endRealPlusEstimatedCost =
        targetTile.__endEstimatedCostRemaining +
        targetTile.__endRealCostFromOrigin;
      targetTile.__endVisited = false;
      targetTile.__endClosed = false;
      targetTile.__endParent = null;
    } else {
      const endNearestPathingGen: Generator<Point, Point, never> = this
        .nearestPathingGen(targetPosition.x, targetPosition.y, entity);

      let next = endNearestPathingGen.next();
      let { x, y } = next.value;

      let tile = this.getTile(
        Math.round((x - offset) * this.resolution),
        Math.round((y - offset) * this.resolution),
      );
      if (!tile) {
        endBest = endHeap[0] || targetTile;
      } else {
        entityAtTile.position.x = this.xTileToWorld(tile.x);
        entityAtTile.position.y = this.yTileToWorld(tile.y);
        const maxEndEstimate = "position" in target
          ? distanceBetweenEntities(entityAtTile, target) * this.resolution
          : h(targetReal, tile);

        // TODO: This is unbounded and does not scale!
        while (
          tile &&
          ("position" in target
            ? (entityAtTile.position.x = this.xTileToWorld(tile.x),
              entityAtTile.position.y = this.yTileToWorld(tile.y),
              distanceBetweenEntities(entityAtTile, target) * this.resolution <=
                maxEndEstimate)
            : h(targetReal, tile) <= maxEndEstimate)
        ) {
          if (cache._pathable(minimalTilemap, tile.x, tile.y)) {
            endTiles.add(tile);
            endHeap.push(tile);
            tile.__endTag = endTag;
            tile.__endRealCostFromOrigin = 0;
            tile.__endEstimatedCostRemaining = h(tile, startReal);
            tile.__endRealPlusEstimatedCost = tile.__endEstimatedCostRemaining +
              tile.__endRealCostFromOrigin;
            tile.__endVisited = false;
            tile.__endClosed = false;
            tile.__endParent = null;
          }

          // Prime next
          if (next.done) break;
          next = endNearestPathingGen.next();
          ({ x, y } = next.value);
          tile = this.getTile(
            Math.round((x - offset) * this.resolution),
            Math.round((y - offset) * this.resolution),
          );
        }
        endBest = endHeap[0] || targetTile;
      }
    }

    const startHeap = new BinaryHeap(
      (node: Tile) => node.__startRealPlusEstimatedCost ?? 0,
    );
    // This won't desync anything.
    // eslint-disable-next-line no-restricted-syntax
    const startTag = Math.random();
    let startBest = startTile;
    startHeap.push(startTile);
    startTile.__startTag = startTag;
    startTile.__startRealCostFromOrigin = h(startReal, startTile);
    startTile.__startEstimatedCostRemaining = h(startTile, endBest);
    startTile.__startRealPlusEstimatedCost =
      startTile.__startEstimatedCostRemaining +
      startTile.__startRealCostFromOrigin;
    startTile.__startVisited = false;
    startTile.__startClosed = false;
    startTile.__startParent = null;

    let checksSinceBestChange = 0;
    let unitIterations = 0;
    const unitBudget = this.getPlayerPathingBudgetRemaining(entity.owner);
    while (startHeap.length) {
      // Degenerate case: target is close to start, but ~blocked off
      if (++checksSinceBestChange > 500) break;

      // Per-unit budget (at least MIN_UNIT_BUDGET, or remaining player budget)
      const overPlayerBudget = this.trackPathingIteration(entity.owner);
      if (++unitIterations > unitBudget || overPlayerBudget) break;

      // Start to End
      const startCurrent = startHeap.pop();

      if (endTiles.has(startCurrent)) {
        startBest = startCurrent;
        break;
      } else if (startCurrent.__endTag === endTag) {
        startBest = endBest = startCurrent;
        break;
      } else if (
        typeof distanceFromTarget === "number" &&
        (entityAtTile.position.x = startCurrent.xWorld,
          entityAtTile.position.y = startCurrent.yWorld,
          "position" in target
            ? distanceBetweenEntities(
                  entityAtTile,
                  target,
                  distanceFromTarget / this.resolution,
                ) * this.resolution < distanceFromTarget
            : distanceBetweenPoints(
                  entityAtTile.position,
                  target,
                ) * this.resolution < distanceFromTarget)
      ) {
        startBest = startCurrent;
        break;
      }

      startCurrent.__startClosed = true;

      const startNeighbors = this.getNeighbors(startCurrent);

      for (let i = 0, length = startNeighbors.length; i < length; i++) {
        const neighbor = startNeighbors[i];

        if (neighbor.__startTag !== startTag) {
          neighbor.__startTag = startTag;
          neighbor.__startEstimatedCostRemaining = 0;
          neighbor.__startRealPlusEstimatedCost = 0;
          neighbor.__startRealCostFromOrigin = 0;
          neighbor.__startVisited = false;
          neighbor.__startClosed = false;
          neighbor.__startParent = null;
        }

        const wasVisited = neighbor.__startVisited;

        if (!wasVisited) {
          if (neighbor.__startClosed || !neighbor.pathable(pathing)) {
            continue;
          } else if (
            !cache._pathable(minimalTilemap, neighbor.x, neighbor.y)
          ) {
            neighbor.__startClosed = true;
            continue;
          }
        }

        const gScore = (startCurrent.__startRealCostFromOrigin ?? 0) + 1;

        // Line of sight test (this is laggy)
        if (
          startCurrent.__startParent &&
          cache._linearPathable(
            entity,
            startCurrent.__startParent,
            neighbor,
          )
        ) {
          const gScore =
            (startCurrent.__startParent.__startRealCostFromOrigin ??
              0) + h(startCurrent.__startParent, neighbor);
          // First visit or better score than previously known
          if (
            !neighbor.__startVisited ||
            gScore < (neighbor.__startRealCostFromOrigin ?? 0)
          ) {
            neighbor.__startVisited = true;
            neighbor.__startParent = startCurrent.__startParent;
            neighbor.__startEstimatedCostRemaining =
              neighbor.__startEstimatedCostRemaining! ||
              h(neighbor, targetReal);
            neighbor.__startRealCostFromOrigin = gScore;
            neighbor.__startRealPlusEstimatedCost =
              neighbor.__startRealCostFromOrigin +
              neighbor.__startEstimatedCostRemaining;

            if (
              neighbor.__startEstimatedCostRemaining <
                (startBest.__startEstimatedCostRemaining ??
                  0) ||
              (neighbor.__startEstimatedCostRemaining ===
                  startBest.__startEstimatedCostRemaining &&
                neighbor.__startRealCostFromOrigin <
                  (startBest.__startRealCostFromOrigin ?? 0))
            ) {
              startBest = neighbor;
              checksSinceBestChange = 0;
            }

            if (!wasVisited) startHeap.push(neighbor);
            else {
              const index = startHeap.indexOf(neighbor);
              if (index >= 0) startHeap.sinkDown(index);
            }
          }

          // First visit or better score than previously known
        } else if (
          !neighbor.__startVisited ||
          gScore < (neighbor.__startRealCostFromOrigin ?? 0)
        ) {
          neighbor.__startVisited = true;
          neighbor.__startParent = startCurrent;
          neighbor.__startEstimatedCostRemaining =
            neighbor.__startEstimatedCostRemaining! ||
            h(neighbor, targetReal);
          neighbor.__startRealCostFromOrigin = gScore;
          neighbor.__startRealPlusEstimatedCost =
            neighbor.__startRealCostFromOrigin +
            neighbor.__startEstimatedCostRemaining;

          if (
            neighbor.__startEstimatedCostRemaining <
              (startBest.__startEstimatedCostRemaining ?? 0) ||
            (neighbor.__startEstimatedCostRemaining ===
                startBest.__startEstimatedCostRemaining &&
              neighbor.__startRealCostFromOrigin <
                (startBest.__startRealCostFromOrigin ?? 0))
          ) {
            startBest = neighbor;
            checksSinceBestChange = 0;
          }

          if (!wasVisited) startHeap.push(neighbor);
          else {
            const index = startHeap.indexOf(neighbor);
            if (index >= 0) startHeap.sinkDown(index);
          }
        }
      }

      // End to Start

      if (!endHeap.length) {
        const { x, y } = this.nearestPathing(
          targetPosition.x,
          targetPosition.y,
          entity,
          (tile) => tile.__endTag !== endTag,
        );
        const newEndtile = this.getTile(
          Math.round((x - offset) * this.resolution),
          Math.round((y - offset) * this.resolution),
        );

        if (newEndtile) {
          endBest = newEndtile;
          endHeap.push(newEndtile);
          newEndtile.__endTag = endTag;
          newEndtile.__endRealCostFromOrigin = h(targetReal, newEndtile);
          newEndtile.__endEstimatedCostRemaining = h(
            newEndtile,
            startReal,
          );
          newEndtile.__endRealPlusEstimatedCost =
            newEndtile.__endEstimatedCostRemaining +
            newEndtile.__endRealCostFromOrigin;
          newEndtile.__endVisited = false;
          newEndtile.__endClosed = false;
          newEndtile.__endParent = null;
        }
      }

      const endCurrent = endHeap.pop();

      if (endCurrent === startTile) {
        endBest = startTile;
        break;
      } else if (endCurrent.__startTag === startTag) {
        startBest = endBest = endCurrent;
        break;
      }

      endCurrent.__endClosed = true;

      const endNeighbors = this.getNeighbors(endCurrent);

      for (let i = 0, length = endNeighbors.length; i < length; i++) {
        const neighbor = endNeighbors[i];

        if (neighbor.__endTag !== endTag) {
          neighbor.__endTag = endTag;
          neighbor.__endEstimatedCostRemaining = 0;
          neighbor.__endRealPlusEstimatedCost = 0;
          neighbor.__endRealCostFromOrigin = 0;
          neighbor.__endVisited = false;
          neighbor.__endClosed = false;
          neighbor.__endParent = null;
        }

        const wasVisited = neighbor.__endVisited;

        if (!wasVisited) {
          if (neighbor.__endClosed || !neighbor.pathable(pathing)) {
            continue;
          } else if (
            !cache._pathable(minimalTilemap, neighbor.x, neighbor.y)
          ) {
            neighbor.__endClosed = true;
            continue;
          }
        }

        const gScore = (endCurrent.__endRealCostFromOrigin ?? 0) + 1;

        // Line of sight test (this is laggy, so disabled ATM)
        if (
          endCurrent.__endParent &&
          cache._linearPathable(
            entity,
            endCurrent.__endParent,
            neighbor,
          )
        ) {
          const gScore = (endCurrent.__endParent.__endRealCostFromOrigin ?? 0) +
            h(endCurrent.__endParent, neighbor);
          // First visit or better score than previously known
          if (
            !neighbor.__endVisited ||
            gScore < (neighbor.__endRealCostFromOrigin ?? 0)
          ) {
            neighbor.__endVisited = true;
            neighbor.__endParent = endCurrent.__endParent;
            neighbor.__endEstimatedCostRemaining =
              neighbor.__endEstimatedCostRemaining! ||
              h(neighbor, startReal);
            neighbor.__endRealCostFromOrigin = gScore;
            neighbor.__endRealPlusEstimatedCost =
              neighbor.__endRealCostFromOrigin +
              neighbor.__endEstimatedCostRemaining;

            if (
              neighbor.__endEstimatedCostRemaining <
                (endBest.__endEstimatedCostRemaining ?? 0) ||
              (neighbor.__endEstimatedCostRemaining ===
                  endBest.__endEstimatedCostRemaining &&
                neighbor.__endRealCostFromOrigin <
                  (endBest.__endRealCostFromOrigin ?? 0))
            ) {
              endBest = neighbor;
              checksSinceBestChange = 0;
            }

            if (!wasVisited) endHeap.push(neighbor);
            else {
              const index = endHeap.indexOf(neighbor);
              if (index >= 0) endHeap.sinkDown(index);
            }
          }

          // First visit or better score than previously known
        } else if (
          !neighbor.__endVisited ||
          gScore < (neighbor.__endRealCostFromOrigin ?? 0)
        ) {
          neighbor.__endVisited = true;
          neighbor.__endParent = endCurrent;
          neighbor.__endEstimatedCostRemaining =
            neighbor.__endEstimatedCostRemaining! ||
            h(neighbor, startReal);
          neighbor.__endRealCostFromOrigin = gScore;
          neighbor.__endRealPlusEstimatedCost =
            neighbor.__endRealCostFromOrigin +
            neighbor.__endEstimatedCostRemaining;

          if (
            neighbor.__endEstimatedCostRemaining <
              (endBest.__endEstimatedCostRemaining ?? 0) ||
            (neighbor.__endEstimatedCostRemaining ===
                endBest.__endEstimatedCostRemaining &&
              neighbor.__endRealCostFromOrigin <
                (endBest.__endRealCostFromOrigin ?? 0))
          ) {
            endBest = neighbor;
            checksSinceBestChange = 0;
          }

          if (!wasVisited) endHeap.push(neighbor);
          else {
            const index = endHeap.indexOf(neighbor);
            if (index >= 0) endHeap.sinkDown(index);
          }
        }
      }
    }

    // if (debugging) {
    // 	elems.forEach((elem) => arena.removeChild(elem));
    // 	elems.splice(0);
    // 	const max = this.grid.reduce(
    // 		(max, row) =>
    // 			row.reduce(
    // 				(max, cell) =>
    // 					Math.max(
    // 						max,
    // 						cell.__startTag === startTag &&
    // 							cell.__startVisited
    // 							? cell.__startRealPlusEstimatedCost ?? 0
    // 							: cell.__endTag === endTag &&
    // 							  cell.__endVisited
    // 							? cell.__endRealPlusEstimatedCost ?? 0
    // 							: -Infinity,
    // 					),
    // 				max,
    // 			),
    // 		-Infinity,
    // 	);
    // 	const min = this.grid.reduce(
    // 		(min, row) =>
    // 			row.reduce(
    // 				(min, cell) =>
    // 					Math.min(
    // 						min,
    // 						cell.__startTag === startTag &&
    // 							cell.__startVisited
    // 							? cell.__startRealPlusEstimatedCost ?? 0
    // 							: cell.__endTag === endTag &&
    // 							  cell.__endVisited
    // 							? cell.__endRealPlusEstimatedCost ?? 0
    // 							: Infinity,
    // 					),
    // 				min,
    // 			),
    // 		Infinity,
    // 	);
    // 	const d = max - min;
    // 	for (let y = 0; y < this.grid.length; y++)
    // 		for (let x = 0; x < this.grid[y].length; x++)
    // 			if (
    // 				(this.grid[y][x].__startTag === startTag &&
    // 					this.grid[y][x].__startVisited) ||
    // 				(this.grid[y][x].__endTag === endTag &&
    // 					this.grid[y][x].__endVisited)
    // 			)
    // 				placeTile(
    // 					x,
    // 					y,
    // 					((this.grid[y][x].__startTag === startTag &&
    // 					this.grid[y][x].__startVisited
    // 						? this.grid[y][x]
    // 								.__startRealPlusEstimatedCost ?? 0
    // 						: this.grid[y][x].__endTag === endTag &&
    // 						  this.grid[y][x].__endVisited
    // 						? this.grid[y][x].__endRealPlusEstimatedCost ??
    // 						  0
    // 						: Infinity) -
    // 						min) /
    // 						d,
    // 				);
    // }

    const pathTiles: Tile[] = [];
    let startCurrent: Tile | null | undefined = startBest;
    while (startCurrent) {
      pathTiles.unshift(startCurrent);
      startCurrent = startCurrent.__startParent;
    }

    if (startBest === endBest) {
      let endCurrent = startBest.__endParent;
      while (endCurrent) {
        pathTiles.push(endCurrent);
        endCurrent = endCurrent.__endParent;
      }
    }

    this._smooth(entity, pathTiles, cache);

    const pathWorld = pathTiles.map((tile) => ({
      x: this.xTileToWorld(tile.x) + offset,
      y: this.yTileToWorld(tile.y) + offset,
    }));

    const last = pathTiles[pathTiles.length - 1];

    const path = [{ x: start.x, y: start.y }];

    if (
      pathWorld.length > 1 &&
      (pathWorld[0].x !== start.x || pathWorld[0].y !== start.y) &&
      !this.linearPathable(entity, start, pathWorld[1])
    ) path.push(pathWorld[0]);

    // const path = pathWorld.length > 1 &&
    //     (pathWorld[0].x !== start.x || pathWorld[0].y !== start.y)
    //   ? this.linearPathable(entity, start, pathWorld[1])
    //     // Can skip first tile since we can path directly to the second
    //     ? [{ x: start.x, y: start.y }]
    //     // Must go through first tile since we cannot path directly to the second
    //     : [{ x: start.x, y: start.y }, pathWorld[0]]
    //   : [pathWorld[0]];

    path.push(...pathWorld.slice(1, -1));

    if (
      !this.linearPathable(entity, path[path.length - 1], targetPosition) ||
      (last !== targetTile)
    ) path.push(pathWorld[pathWorld.length - 1]);

    if (last === targetTile && targetPathable) path.push(targetPosition);

    // Step back with a tolerance between 99% and 100% distance to target
    // E.g., if the passed distance is 100, the path will terminate (if
    // possible) between 99 and 100 units away.
    const tolerance = (distanceFromTarget ?? 0) * 0.01;
    if (
      typeof distanceFromTarget === "number" && "position" in target &&
      path.length > 1 &&
      (entityAtTile.position.x = path[path.length - 1].x,
        entityAtTile.position.y = path[path.length - 1].y,
        distanceBetweenEntities(entityAtTile, target) * this.resolution <
          distanceFromTarget - tolerance)
    ) {
      let a = path[path.length - 1];
      let b = path[path.length - 2];
      let mid;
      let itrs = 0;
      while (itrs < 15) {
        itrs++;
        mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        entityAtTile.position.x = mid.x;
        entityAtTile.position.y = mid.y;
        const currentDistance = distanceBetweenEntities(entityAtTile, target) *
          this.resolution;
        const diff = distanceFromTarget - currentDistance;
        if (diff >= 0 && diff <= tolerance) break;
        if (currentDistance < distanceFromTarget) a = mid;
        else b = mid;
      }
      if (mid && itrs < 15) path[path.length - 1] = mid;
    }

    this._losCtx = undefined;

    if (removed) this.addEntity(entity);
    for (const entity of removedMovingEntities) this.addEntity(entity);

    return path;
  }

  /**
   * Rechecks a path to make sure it's still pathable. Can return false even
   * if the path is still pathable.
   * @param path The array of points that make up the path to be checked.
   * @param entity The object of the path to be checked. Includes clearance
   * (radius) and pathing type.
   * @param amount How far along the path we should check at a minimum. We'll
   * likely overcheck, since we just verify segments of the path.
   * @param offset How far along that path we start checking at maximum.
   */
  recheck(
    path: Point[],
    entity: PathingEntity,
    amount = Infinity,
    offset = 0,
  ): boolean {
    const removed = this.entities.has(entity);
    if (removed) this.removeEntity(entity);

    let cur = 0;
    let distanceSquared = 0;
    const offsetSquared = offset ** 2;
    const amountSqaured = offsetSquared + amount ** 2;
    let segmentLength = (path[1].x - path[0].x) ** 2 +
      (path[1].y - path[0].y) ** 2;

    // Skip parts of the path we aren't rechecking.
    while (
      distanceSquared + segmentLength < offsetSquared &&
      cur < path.length - 2
    ) {
      distanceSquared += segmentLength;
      cur++;
      segmentLength = (path[cur + 1].x - path[cur].x) ** 2 +
        (path[cur + 1].y - path[cur].y) ** 2;
    }

    if (cur === path.length - 1) {
      return this.pathable(entity, path[cur].x, path[cur].y);
    }

    while (cur < path.length - 1 && distanceSquared < amountSqaured) {
      if (!this.linearPathable(entity, path[cur], path[cur + 1])) {
        if (removed) this.addEntity(entity);
        return false;
      }

      distanceSquared += (path[cur + 1].x - path[cur].x) ** 2 +
        (path[cur + 1].y - path[cur].y) ** 2;
      cur++;
    }

    if (removed) this.addEntity(entity);
    return true;
  }

  private _smooth(
    entity: PathingEntity,
    path: Tile[],
    cache: Cache = this,
  ): void {
    for (let skip = path.length - 1; skip > 1; skip--) {
      for (let index = 0; index < path.length - skip; index++) {
        if (
          cache._linearPathable(
            entity,
            path[index],
            path[index + skip],
          )
        ) {
          path.splice(index + 1, skip - 1);
          skip = path.length;
          break;
        }
      }
    }
  }

  /**
   * Internals of ParthingMap#linearPathable. Public for testing purposes.
   */
  _linearPathable(
    entity: PathingEntity,
    startTile: Tile,
    endTile: Tile,
  ): boolean {
    const ctx = this._losCtx;
    if (!ctx) {
      const radiusOffset = entity.radius % (1 / this.resolution);
      return this.linearPathable(
        entity,
        offset({ x: startTile.xWorld, y: startTile.yWorld }, radiusOffset),
        offset({ x: endTile.xWorld, y: endTile.yWorld }, radiusOffset),
      );
    }

    const { radius, pathing, radiusTileOffset } = ctx;

    // Work directly in tile-float coordinates
    let startX = startTile.x + radiusTileOffset;
    let startY = startTile.y + radiusTileOffset;
    let endX = endTile.x + radiusTileOffset;
    let endY = endTile.y + radiusTileOffset;

    // Same-tile early exit
    if (
      Math.floor(startX) === Math.floor(endX) &&
      Math.floor(startY) === Math.floor(endY)
    ) {
      const tileX = Math.floor(startX);
      const tileY = Math.floor(startY);
      const tile = this.grid[tileY]?.[tileX];
      return !!tile && tile.pathable(pathing);
    }

    // Swap so we're going right
    if (startX > endX) {
      const tmpX = startX;
      const tmpY = startY;
      startX = endX;
      startY = endY;
      endX = tmpX;
      endY = tmpY;
    }

    const dx = endX - startX;
    const dy = endY - startY;
    const dist = (dx * dx + dy * dy) ** 0.5;
    const cosA = dx / dist;
    const sinA = dy / dist;
    const tan = dx / dy;
    const positiveSlope = endY > startY;

    const minY = positiveSlope
      ? Math.floor(startY - radius)
      : Math.floor(endY - radius);
    const maxY = positiveSlope
      ? Math.floor(endY + radius)
      : Math.floor(startY + radius);
    const yStart = positiveSlope ? minY : maxY;
    const yStep = positiveSlope ? 1 : -1;
    const ySteps = Math.abs(minY - maxY);

    const minX = Math.floor(startX - radius);
    const maxX = Math.floor(endX + radius);

    // Trig identities: cos(a-π/2)=sinA, sin(a-π/2)=-cosA,
    //                   cos(a+π/2)=-sinA, sin(a+π/2)=cosA
    const sinAR = sinA * radius;
    const cosAR = cosA * radius;
    const leftTangentX = startX + sinAR;
    const leftTangentY = startY - cosAR;
    const rightTangentX = startX - sinAR;
    const rightTangentY = startY + cosAR;
    const endLeftTangentX = endX + sinAR;
    const endLeftTangentY = endY - cosAR;

    // Precompute orientation constants: both tangent pairs share same dy/dx
    const tangentDy = 2 * cosAR;
    const tangentDx = -2 * sinAR;

    const radiusSq = radius * radius;

    const startFloor = positiveSlope
      ? Math.floor(startY - radius) + 1
      : Math.ceil(startY + radius) - 1;

    const rightGuideX = isFinite(tan)
      ? rightTangentX + (startFloor - rightTangentY) * tan
      : rightTangentX - radius;
    const leftGuideX = isFinite(tan)
      ? leftTangentX - (leftTangentY - startFloor) * tan
      : leftTangentX - radius;

    const guide = Math.max(rightGuideX, leftGuideX);
    const guideDistance = Math.abs(rightGuideX - leftGuideX);

    const absTan = Math.abs(tan);
    const finiteAbsTan = isFinite(absTan);
    const totalShift = finiteAbsTan ? absTan + guideDistance : guideDistance;

    let xStartRaw = guide - totalShift;
    for (let y = 0; y <= ySteps; y++) {
      const xEndRaw = xStartRaw + (finiteAbsTan ? totalShift : Infinity);

      const xStartTest = Math.floor(xStartRaw);
      const xEndTest = Math.floor(xEndRaw);

      const yCoord = yStart + y * yStep;

      // Inline behind: orientation(left, right, {x,y}) < 0
      // = (x - ltX) * tangentDy - (y - ltY) * tangentDx < 0
      const xStartMin = xStartRaw < startX &&
          (xStartTest + 1 - leftTangentX) * tangentDy -
                (yCoord - leftTangentY) * tangentDx <
            0
        ? trueMinXRaw(
          startX,
          startY,
          radiusSq,
          yCoord,
          Math.max(xStartTest, minX),
        )
        : -Infinity;

      // Inline infront: orientation(endLeft, endRight, {x,y}) > 0
      const xEndMin = xStartRaw < endX &&
          (xStartTest - 1 - endLeftTangentX) * tangentDy -
                (yCoord - endLeftTangentY) * tangentDx >
            0
        ? trueMinXRaw(
          endX,
          endY,
          radiusSq,
          yCoord,
          Math.max(xStartTest, minX),
        )
        : -Infinity;

      const xStart = Math.max(xStartMin, xEndMin, xStartTest, minX);

      const xStartMax = xEndRaw > startX &&
          (xEndTest + 1 - leftTangentX) * tangentDy -
                (yCoord - leftTangentY) * tangentDx <
            0
        ? trueMaxXRaw(
          startX,
          startY,
          radiusSq,
          yCoord,
          Math.min(xEndTest, maxX),
        )
        : Infinity;

      const xEndMax = xEndRaw > endX &&
          (xEndTest - 1 - endLeftTangentX) * tangentDy -
                (yCoord - endLeftTangentY) * tangentDx >
            0
        ? trueMaxXRaw(
          endX,
          endY,
          radiusSq,
          yCoord,
          Math.min(xEndTest, maxX),
        )
        : Infinity;

      const xEnd = Math.min(xStartMax, xEndMax, xEndTest, maxX);

      const row = this.grid[yCoord];
      if (!row) return false;
      for (let x = xStart; x <= xEnd; x++) {
        const tile = row[x];
        if (!tile || !tile.pathable(pathing)) return false;
      }

      xStartRaw += finiteAbsTan ? absTan : 0;
    }

    return true;
  }

  entityToTileCoordsBounded(
    entity: PathingEntity,
    position: Point = entity.position,
  ): { x: number; y: number } {
    const nudge = EPSILON * entity.radius * this.widthWorld;
    return {
      x: this.xBoundTile(
        Math.round(position.x * this.resolution - nudge),
      ),
      y: this.yBoundTile(
        Math.round(position.y * this.resolution - nudge),
      ),
    };
  }

  // BAD?
  entityToTile(entity: PathingEntity, position: Point = entity.position): Tile {
    const { x, y } = this.entityToTileCoordsBounded(entity, position);
    const tile = this.getTile(x, y);
    if (!tile) {
      throw new Error(
        `entityToTile called with out-of-bounds coordinates: (${position.x}, ${position.y})`,
      );
    }
    return tile;
  }

  /**
   * Checks whether an entity is clearance to go from `startWorld` to
   * `endWorld`.
   */
  linearPathable(
    entity: PathingEntity,
    startWorld: Point,
    endWorld: Point,
  ): boolean {
    // Restrictions + pull fields off entity
    if (typeof entity.radius !== "number") {
      throw new Error("Can only path find radial entities");
    }
    const radius = entity.radius * this.resolution -
      EPSILON *
        entity.radius *
        this.widthWorld *
        this.resolution;
    const pathing = entity.requiresPathing !== undefined
      ? entity.requiresPathing
      : entity.pathing;
    if (pathing === undefined) throw "entity has no pathing";

    // Swap so we're going right
    [startWorld, endWorld] = startWorld.x <= endWorld.x
      ? [startWorld, endWorld]
      : [endWorld, startWorld];

    {
      const startTile = this.worldToTile(startWorld);
      const endTile = this.worldToTile(endWorld);

      if (startTile === endTile) {
        const map = entity.requiresTilemap ?? entity.tilemap ??
          this.pointToTilemap(startWorld.x, startWorld.y, entity.radius, {
            type: pathing,
          });

        return this.withoutEntity(
          entity,
          () => this._pathable(map, startTile.x, startTile.y),
        );
      }
    }

    const startPX = startWorld.x * this.resolution;
    const startPY = startWorld.y * this.resolution;
    const endPX = endWorld.x * this.resolution;
    const endPY = endWorld.y * this.resolution;

    const lpDx = endPX - startPX;
    const lpDy = endPY - startPY;
    const lpDist = (lpDx * lpDx + lpDy * lpDy) ** 0.5;
    const cosA = lpDx / lpDist;
    const sinA = lpDy / lpDist;
    const tan = lpDx / lpDy;
    const positiveSlope = endPY > startPY;

    const minY = positiveSlope
      ? Math.floor(startPY - radius)
      : Math.floor(endPY - radius);
    const maxY = positiveSlope
      ? Math.floor(endPY + radius)
      : Math.floor(startPY + radius);
    const yStart = positiveSlope ? minY : maxY;
    const yStep = positiveSlope ? 1 : -1;
    const ySteps = Math.abs(minY - maxY);

    const minX = Math.floor(startPX - radius);
    const maxX = Math.floor(endPX + radius);

    // Trig identities: cos(a-π/2)=sinA, sin(a-π/2)=-cosA,
    //                   cos(a+π/2)=-sinA, sin(a+π/2)=cosA
    const sinAR = sinA * radius;
    const cosAR = cosA * radius;
    const leftTangentX = startPX + sinAR;
    const leftTangentY = startPY - cosAR;
    const rightTangentX = startPX - sinAR;
    const rightTangentY = startPY + cosAR;
    const endLeftTangentX = endPX + sinAR;
    const endLeftTangentY = endPY - cosAR;

    // Precompute orientation constants: both tangent pairs share same dy/dx
    const tangentDy = 2 * cosAR;
    const tangentDx = -2 * sinAR;

    const radiusSq = radius * radius;

    const startFloor = positiveSlope
      ? Math.floor(startPY - radius) + 1
      : Math.ceil(startPY + radius) - 1;

    const rightGuideX = isFinite(tan)
      ? rightTangentX + (startFloor - rightTangentY) * tan
      : rightTangentX - radius;
    const leftGuideX = isFinite(tan)
      ? leftTangentX - (leftTangentY - startFloor) * tan
      : leftTangentX - radius;

    const guide = Math.max(rightGuideX, leftGuideX);
    const guideDistance = Math.abs(rightGuideX - leftGuideX);

    const absTan = Math.abs(tan);
    const finiteAbsTan = isFinite(absTan);
    const totalShift = finiteAbsTan ? absTan + guideDistance : guideDistance;

    let xStartRaw = guide - totalShift;
    for (let y = 0; y <= ySteps; y++) {
      const xEndRaw = xStartRaw + (finiteAbsTan ? totalShift : Infinity);

      const xStartTest = Math.floor(xStartRaw);
      const xEndTest = Math.floor(xEndRaw);

      const yCoord = yStart + y * yStep;

      const xStartMin = xStartRaw < startPX &&
          (xStartTest + 1 - leftTangentX) * tangentDy -
                (yCoord - leftTangentY) * tangentDx <
            0
        ? trueMinXRaw(
          startPX,
          startPY,
          radiusSq,
          yCoord,
          Math.max(xStartTest, minX),
        )
        : -Infinity;

      const xEndMin = xStartRaw < endPX &&
          (xStartTest - 1 - endLeftTangentX) * tangentDy -
                (yCoord - endLeftTangentY) * tangentDx >
            0
        ? trueMinXRaw(
          endPX,
          endPY,
          radiusSq,
          yCoord,
          Math.max(xStartTest, minX),
        )
        : -Infinity;

      const xStart = Math.max(xStartMin, xEndMin, xStartTest, minX);

      const xStartMax = xEndRaw > startPX &&
          (xEndTest + 1 - leftTangentX) * tangentDy -
                (yCoord - leftTangentY) * tangentDx <
            0
        ? trueMaxXRaw(
          startPX,
          startPY,
          radiusSq,
          yCoord,
          Math.min(xEndTest, maxX),
        )
        : Infinity;

      const xEndMax = xEndRaw > endPX &&
          (xEndTest - 1 - endLeftTangentX) * tangentDy -
                (yCoord - endLeftTangentY) * tangentDx >
            0
        ? trueMaxXRaw(
          endPX,
          endPY,
          radiusSq,
          yCoord,
          Math.min(xEndTest, maxX),
        )
        : Infinity;

      const xEnd = Math.min(xStartMax, xEndMax, xEndTest, maxX);

      const row = this.grid[yCoord];
      if (!row) return false;
      for (let x = xStart; x <= xEnd; x++) {
        const tile = row[x];
        if (!tile || !tile.pathable(pathing)) return false;
      }

      xStartRaw += finiteAbsTan ? absTan : 0;
    }

    return true;
  }

  /**
   * Adds an entity to the PathingMap, adding it to any tiles it intersects
   * with.
   */
  addEntity(entity: PathingEntity): void {
    const tiles = [];
    const position = entity.position;
    const { map, top, left, width, height } = entity.tilemap ??
      this.pointToTilemap(position.x, position.y, entity.radius, {
        type: entity.blocksPathing ?? entity.pathing,
      });
    const tileX = this.xWorldToTile(position.x);
    const tileY = this.yWorldToTile(position.y);
    for (let y = top; y < top + height; y++) {
      for (let x = left; x < left + width; x++) {
        const tx = tileX + x;
        const ty = tileY + y;
        if (ty < 0 || ty >= this.heightMap || tx < 0 || tx >= this.widthMap) {
          continue;
        }
        const tile = this.getTile(tx, ty);
        if (!tile) continue;
        tiles.push(tile);
        tile.addEntity(
          entity,
          map[(y - top) * width + (x - left)],
        );
      }
    }

    this.entities.set(entity, tiles);
  }

  /**
   * Notifies the PathingMap the entity may occupy a new tiles, removing
   * it from tiles it no longer intersects and adding it to tiles it now
   * intersects.
   * Note: This will not reflect changes to the entity's pathing type. An
   * entity's pathing type is treated as immutable.
   */
  updateEntity(entity: PathingEntity): void {
    if (!this.entities.has(entity)) return;
    const oldTiles: Tile[] = this.entities.get(entity) ?? [];
    const newTiles: Tile[] = [];
    const newTileMapValues: number[] = [];
    const position = entity.position;
    const { map, top, left, width, height } = entity.tilemap ??
      this.pointToTilemap(position.x, position.y, entity.radius, {
        type: entity.blocksPathing ?? entity.pathing,
      });
    const tileX = this.xWorldToTile(position.x);
    const tileY = this.yWorldToTile(position.y);
    for (let y = top; y < top + height; y++) {
      for (let x = left; x < left + width; x++) {
        const gridY = tileY + y;
        const gridX = tileX + x;
        // Check bounds before accessing grid
        if (
          gridY >= 0 && gridY < this.heightMap &&
          gridX >= 0 && gridX < this.widthMap
        ) {
          const tile = this.getTile(gridX, gridY);
          if (tile) {
            newTiles.push(tile);
            newTileMapValues.push(map[(y - top) * width + (x - left)]);
          }
        }
      }
    }

    // Tiles that the entity no longer occupies
    oldTiles
      .filter((t) => !newTiles.includes(t))
      .forEach((tile) => tile.removeEntity(entity));

    newTiles.forEach((tile, index) => {
      // Tiles the entity continues to occupy
      if (oldTiles.includes(tile)) {
        tile.updateEntity(entity, newTileMapValues[index]);
      } // Tiles the entity now occupies
      else tile.addEntity(entity, newTileMapValues[index]);
    });

    this.entities.set(entity, newTiles);
  }

  /**
   * Find a path that may require destroying entities.
   *
   * Unlike path(), this uses unidirectional A* and can pass through
   * entities that the canDestroy callback approves. Returns both the
   * path and the list of entities that must be destroyed (in order).
   *
   * @param entity The entity to path
   * @param target Target position
   * @param canDestroy Callback that returns destruction cost for an entity,
   *                   or undefined if the entity cannot be destroyed
   * @param maxCost Maximum path cost before bailing out (default 100)
   * @returns Path and entities to destroy, or undefined if no path exists
   */
  pathWithDestruction(
    entity: PathingEntity,
    target: Readonly<Point>,
    canDestroy: (e: PathingEntity) => number | undefined,
    maxCost = 100,
  ): { path: Point[]; toDestroy: PathingEntity[] } | undefined {
    if (typeof entity.radius !== "number") {
      throw new Error("Can only path find radial entities");
    }

    const removed = this.entities.has(entity);
    if (removed) this.removeEntity(entity);

    try {
      const pathing = entity.requiresPathing ?? entity.pathing;
      if (pathing === undefined) return undefined;

      const minimalTilemap = this.pointToTilemap(
        entity.radius,
        entity.radius,
        entity.radius,
        { type: pathing },
      );
      const entityOffset = entity.radius % (1 / this.resolution);

      const startTile = this.entityToTile(entity);
      const targetTile = this.entityToTile(entity, target);
      if (!startTile || !targetTile) return undefined;

      const targetReal = {
        x: target.x * this.resolution,
        y: target.y * this.resolution,
      };

      // Node type for our search - tracks destroyed entities per path
      type Node = {
        tile: Tile;
        g: number;
        f: number;
        parent: Node | null;
        destroyed: Set<PathingEntity>;
      };

      const heuristic = (tile: Tile) => h(tile, targetReal);

      const startNode: Node = {
        tile: startTile,
        g: 0,
        f: heuristic(startTile),
        parent: null,
        destroyed: new Set(),
      };

      const open = new BinaryHeap<Node>((n) => n.f);
      open.push(startNode);

      // Track best cost to reach each tile
      const bestG = new Map<Tile, number>();
      bestG.set(startTile, 0);

      // Track best partial path (closest to target)
      let bestPartial: Node | null = null;
      let bestPartialH = Infinity;

      const maxIterations = 2000;
      let iterations = 0;

      while (open.length > 0 && iterations++ < maxIterations) {
        const current = open.pop();

        // Track best partial path (node closest to target)
        const currentH = heuristic(current.tile);
        if (currentH < bestPartialH) {
          bestPartialH = currentH;
          bestPartial = current;
        }

        // Bail out if cost exceeds maximum
        if (current.g > maxCost) continue;

        // Reached target?
        if (current.tile === targetTile) {
          // Build path
          const pathTiles: Tile[] = [];
          let node: Node | null = current;
          while (node) {
            pathTiles.unshift(node.tile);
            node = node.parent;
          }

          // Convert to world coordinates
          const path = pathTiles.map((tile) => ({
            x: this.xTileToWorld(tile.x) + entityOffset,
            y: this.yTileToWorld(tile.y) + entityOffset,
          }));

          // Collect destroyed entities in order by walking the path
          const toDestroy: PathingEntity[] = [];
          const seen = new Set<PathingEntity>();
          const nodesInOrder: Node[] = [];
          node = current;
          while (node) {
            nodesInOrder.unshift(node);
            node = node.parent;
          }
          for (const n of nodesInOrder) {
            for (const e of n.destroyed) {
              if (!seen.has(e)) {
                seen.add(e);
                toDestroy.push(e);
              }
            }
          }

          return { path, toDestroy };
        }

        // Explore neighbors
        const neighbors = this.getNeighbors(current.tile);

        for (const neighborTile of neighbors) {
          let moveCost = 1;
          let newDestroyed = current.destroyed;
          let passable = neighborTile.pathable(pathing) &&
            this._pathable(minimalTilemap, neighborTile.x, neighborTile.y);

          if (!passable) {
            // Blocked by terrain - impassable
            if ((neighborTile.originalPathing & pathing) !== 0) continue;

            // Check what entities are blocking and if we can destroy them
            const blockers: Array<{ entity: PathingEntity; cost: number }> = [];
            let canPass = true;

            for (
              const [blockingEntity, entityPathing] of neighborTile.entities
            ) {
              if ((entityPathing & pathing) !== 0) {
                // Already committed to destroying this one
                if (current.destroyed.has(blockingEntity)) continue;

                const cost = canDestroy(blockingEntity);
                if (cost === undefined) {
                  canPass = false;
                  break;
                }
                blockers.push({ entity: blockingEntity, cost });
              }
            }

            if (!canPass) continue;

            passable = true;
            if (blockers.length > 0) {
              newDestroyed = new Set(current.destroyed);
              for (const { entity: blocker, cost } of blockers) {
                newDestroyed.add(blocker);
                moveCost += cost;
              }
            }
          }

          if (!passable) continue;

          const g = current.g + moveCost;

          // Skip if exceeds max cost
          if (g > maxCost) continue;

          // Skip if we've found a better path to this tile
          const existingG = bestG.get(neighborTile);
          if (existingG !== undefined && existingG <= g) continue;
          bestG.set(neighborTile, g);

          open.push({
            tile: neighborTile,
            g,
            f: g + heuristic(neighborTile),
            parent: current,
            destroyed: newDestroyed,
          });
        }
      }

      // No complete path found - return best partial path if it has structures to destroy
      if (bestPartial && bestPartial.destroyed.size > 0) {
        const pathTiles: Tile[] = [];
        let node: Node | null = bestPartial;
        while (node) {
          pathTiles.unshift(node.tile);
          node = node.parent;
        }

        const path = pathTiles.map((tile) => ({
          x: this.xTileToWorld(tile.x) + entityOffset,
          y: this.yTileToWorld(tile.y) + entityOffset,
        }));

        const toDestroy: PathingEntity[] = [];
        const seen = new Set<PathingEntity>();
        const nodesInOrder: Node[] = [];
        node = bestPartial;
        while (node) {
          nodesInOrder.unshift(node);
          node = node.parent;
        }
        for (const n of nodesInOrder) {
          for (const e of n.destroyed) {
            if (!seen.has(e)) {
              seen.add(e);
              toDestroy.push(e);
            }
          }
        }

        return { path, toDestroy };
      }

      return undefined;
    } finally {
      if (removed) this.addEntity(entity);
    }
  }

  /**
   * Removes the entity from the PathingMap, clearing it from all tiles.
   */
  removeEntity(entity: Entity): void {
    const tiles = this.entities.get(entity as PathingEntity);
    if (tiles) tiles.forEach((tile) => tile.removeEntity(entity));
    this.entities.delete(entity as PathingEntity);
  }

  getExistingTile(x: number, y: number): Tile | undefined {
    return this.grid[y]?.[x];
  }

  getEntityTiles(entity: PathingEntity): readonly Tile[] | undefined {
    return this.entities.get(entity);
  }

  // paint(): void {
  // 	const host =
  // 		this._elem ||
  // 		(this._elem = (() => {
  // 			const elem = document.createElement("div");
  // 			arena.appendChild(elem);

  // 			return elem;
  // 		})());

  // 	emptyElement(host);
  // 	const cellSize = 32 / this.resolution;

  // 	for (let y = 0; y < this.heightMap; y++)
  // 		for (let x = 0; x < this.widthMap; x++) {
  // 			const cell = document.createElement("div");
  // 			Object.assign(cell.style, {
  // 				zIndex: 10,
  // 				position: "absolute",
  // 				top: `${y * cellSize}px`,
  // 				left: `${x * cellSize}px`,
  // 				width: `${cellSize}px`,
  // 				height: `${cellSize}px`,
  // 				background: `rgba(${
  // 					this.grid[y][x].pathing & 1 ? 255 : 0
  // 				}, 0, ${this.grid[y][x].pathing & 2 ? 255 : 0}, 0.4)`,
  // 			});
  // 			host.appendChild(cell);
  // 		}
  // }

  // paintMap(map: Footprint, xTile: number, yTile: number): void {
  // 	const host =
  // 		this._elem ||
  // 		(this._elem = (() => {
  // 			const elem = document.createElement("div");
  // 			arena.appendChild(elem);

  // 			return elem;
  // 		})());

  // 	const cellSize = 32 / this.resolution;

  // 	let i = 0;

  // 	for (let y = yTile + map.top; y < yTile + map.height + map.top; y++)
  // 		for (
  // 			let x = xTile + map.left;
  // 			x < xTile + map.width + map.left;
  // 			x++, i++
  // 		) {
  // 			const cell = document.createElement("div");
  // 			Object.assign(cell.style, {
  // 				zIndex: 10,
  // 				position: "absolute",
  // 				top: `${y * cellSize}px`,
  // 				left: `${x * cellSize}px`,
  // 				width: `${cellSize}px`,
  // 				height: `${cellSize}px`,
  // 				background:
  // 					this.grid[y] === undefined ||
  // 					this.grid[y][x] === undefined ||
  // 					this.grid[y][x].pathing & map.map[i]
  // 						? "rgba(255,0,0,0.5)"
  // 						: "rgba(0,255,0,0.5)",
  // 			});
  // 			cell.setAttribute("x", x.toString());
  // 			cell.setAttribute("y", y.toString());
  // 			cell.setAttribute("i", i.toString());
  // 			cell.setAttribute(
  // 				"grid",
  // 				(this.grid[y] === undefined
  // 					? "no-y"
  // 					: this.grid[y][x] === undefined
  // 					? "no-x"
  // 					: this.grid[y][x].pathing
  // 				).toString(),
  // 			);
  // 			cell.setAttribute("map", map.map[i].toString());
  // 			host.appendChild(cell);
  // 		}
  // }
}
